import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { CommonModule } from '@angular/common';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { ConfirmDialogComponent } from '@shared/ui/confirm-dialog/confirm-dialog.component';
import { BehaviorSubject, Observable, Subscription, take } from 'rxjs';
import { NotificationFacade } from '@core/facades/notification.facade';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { Pagination } from '@core/models/pagination.model';
import { AllRolesDatum } from '@core/models/admin/roles-and-permissions';
import { dropdownMenuAnimation } from '@shared/animations/animations';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { IFILTER } from 'app/app.constants';
import { Store } from '@ngxs/store';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { UpdatedPermissions } from './pages/add-role/add-role.component';
import { IRoles } from 'app/auth/table-store/table.state.model';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { PermissionDirective } from '@shared/directives/permission.directive';
import { AuthActions } from 'app/auth/store/auth.actions';
@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [
    CommonModule,
    ButtonDirective,
    RouterLink,
    RouterOutlet,
    ButtonDirective,
    DropdownComponent,
    TableNavigatorComponent,
    FiltersComponent,
    TableSkeletonLoaderComponent,
    PermissionDirective,
  ],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
  animations: [dropdownMenuAnimation],
})
export class RolesComponent implements OnInit, OnDestroy {
  filterMenuState: 'visible' | 'hidden' = 'hidden';
  menuRow!: number | null;
  isLoading: boolean = false;
  isDialogOpen: boolean = false;
  isDialogLoading: boolean = false;
  subs: Subscription[] = [];
  rolesList: AllRolesDatum[] = [];
  roleId!: string | null;
  roleName!: string | null;
  totalCount!: number;
  currentPage: number = 1;
  rolesListSubject = new BehaviorSubject<any>(null);
  totalCountSubject = new BehaviorSubject<number>(0);
  rolesList$ = this.rolesListSubject.asObservable();
  totalCount$: Observable<number> = this.totalCountSubject.asObservable();
  isFetchingRolesList$!: Observable<boolean>;
  hadFilterBefore: Boolean = false;
  permissionListSubject = new BehaviorSubject<any>(null);
  permissionList$ = this.permissionListSubject.asObservable();
  permissionList!: UpdatedPermissions[];
  groupedPermissions: { [key: string]: any[] } = {};
  permissionGroupState: { [key: string]: boolean } = {};
  isFetchingPermissions$!: Observable<boolean>;
  filteredGroupedPermissions: { [key: string]: any[] } = {};
  searchKeyword: string = '';

  breadCrumbService = inject(BreadCrumbService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);
  rolePermissionService = inject(RolesAndPermissionsService);
  notify = inject(NotificationFacade);
  store = inject(Store);
  confirmDialogService = inject(ConfirmDialogService);

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },
    {
      filterKey: 'type',
      label: 'Type',
      type: 'dropdown',
      dropdown: [
        { key: 'admin', value: 'Admin' },
        { key: 'client', value: 'Client' },
      ],
    },
  ];

  constructor() {
    this.rolesList$ = this.store.select(TableSelectors.getTableData('roles'));
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('roles')
    );
    this.permissionList$ = this.store.select(TableSelectors.getPermissions());
    this.isFetchingRolesList$ = this.store.select(
      TableSelectors.getTableLoading('roles')
    );
  }

  ngOnInit(): void {
    this.breadCrumbService.setPageBreadCrumb(this.route);
    this.getAllPermissions();
    this.loadRolesList();
    this.rolesFiltersEventListener();
    this.listenToPermissionSearch();
    const sub = this.permissionList$.subscribe((permission) => {
      if (permission) {
        this.groupPermissionsByPathSegment(permission);
        this.cdr.detectChanges();
      }
    });
    this.subs.push(sub);

  }

  loadRolesList(page = 1) {
    const sub = this.rolesList$.pipe(take(1)).subscribe((role) => {
      const hasData = Array.isArray(role) && role.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'roles',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  rolesFiltersEventListener() {
    this.route.queryParams.subscribe((params) => {
      let query = '';
      if (params[this.filterStructure[0].filterKey]) {
        query = `start_date=${params['start_date']}&`;
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `end_date=${params['end_date']}&`;
      }
      if (params[this.filterStructure[2].filterKey]) {
        query += `type=${params['type']}&`;
      }
     

      // Handle pagination
      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        query += `skip=${skip}&take=${take}`;
        this.hadFilterBefore = true; // filters are currently active
        this.store.dispatch(new TableActions.FilterRoleList({ filter: query }));
      } else {
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'roles',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
  }

  toggleFilterMenu() {
    this.filterMenuState =
      this.filterMenuState === 'hidden' ? 'visible' : 'hidden';
  }

  roleDropdownActions(role: AllRolesDatum): DropDownModel[] {
    return [
      {
        title: 'View Details',
        action: () => {
          this.router.navigate([`admin/roles/view-role/${role.id}`], {
            state: { role },
          });
        },
        show: true && this.rolePermissionService.hasPermission(['update_roles']),
      },
      {
        title: 'Edit',
        action: () => {
          this.router.navigate([`admin/roles/edit-role/${role.id}`], {
            state: { role },
          });
        },
        show: true && this.rolePermissionService.hasPermission(['update_roles']),
      },
      {
        title: 'Delete',
        action: () => {
          this.roleId = role.id;
          this.roleName = role.name;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Delete Role',
            description: `Are you sure you want to delete the: '${this.roleName}' role? This action cannot be undone.`,
            loadingText: 'Deleting Role...',
            bgConfirmButton: 'danger',
            action: () => this.deleteRole(),
            onClose: () => {
              this.roleId = null;
              this.roleName = null;
            },
          });
        },
        show: true && this.rolePermissionService.hasPermission(['delete_role']),
      },
    ];
  }

  getCurrentPageRoles() {
    const sub = this.route.queryParams.subscribe({
      next: (param) => {
        this.currentPage = Number.parseInt(param['page'] || '1');
        const skip = this.currentPage === 1 ? 0 : this.currentPage * 10 - 10;
        this.getAllRoles();
      },
    });
    this.subs.push(sub);
  }

  getAllRoles() {
    this.isLoading = true;
    const sub = this.rolePermissionService.getAllRoles().subscribe({
      next: (response) => {
        this.rolesList = response.data.data;
        this.totalCount = response.data.total_count;

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;

        this.cdr.detectChanges();
      },
    });

    this.subs.push(sub);
  }

  getAllPermissions() {
    const sub = this.permissionList$.pipe(take(1)).subscribe((permission) => {
      const hasData = Array.isArray(permission) && permission.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(new TableActions.GetPermissions());
      }
      // permisssion fetching and grouping initiated
      this.isFetchingPermissions$ = new Observable<true>;
    });
    this.subs.push(sub);
  }

  deleteRole() {
    this.confirmDialogService.updateDialog({ isDialogLoading: true });
    let roleId = '';
    this.roleId ? (roleId = this.roleId) : null;
    this.store.dispatch(new TableActions.DeleteRole({ roleId: roleId }));
    this.resetDialog();
  }

  resetDialog() {
    this.confirmDialogService.updateDialog({
      isDialogLoading: false,
      isOpen: false,
    });
    this.roleId = null;
  }

  hasPermission(role: IRoles, permission: any): boolean {
    // if (!Array.isArray(role.permissions)) return false;
    for (const rolePermission of role.permissions) {
      if (rolePermission.permission?.id === permission?.id) {
        return true;
      }
    }
    return false;
  }
  navigate(currentPage: number) {
    this.getAllRoles();
  }

  togglePermission(role: IRoles, permission: any) {
    const hasPerm = this.hasPermission(role, permission);
    if (hasPerm) {
      this.removePermission(permission, role.id);
    } else {
      this.addPermissionToRole(permission, role.id);
    }
  }

  addPermissionToRole(permission: any, role_id: string) {

    const sub = this.rolePermissionService
      .addPermissionToRole({ permission_id: permission.id, role_id: role_id })
      .subscribe({
        next: (response) => {
          this.notify.success('Permission added successfully');
          this.store.dispatch(
            new TableActions.UpdateRolePermission({
              roleId: role_id,
              permission: permission,
              isAdd: true,
            })
          );
          this.store.dispatch(new AuthActions.LoadPermissions())
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.notify.error(err.error.message);
        },
      });

    this.subs.push(sub);
  }

  removePermission(permission: any, roleId: string) {
    const sub = this.rolePermissionService
      .removePermissionFromRole(permission.id, roleId)
      .subscribe({
        next: (response) => {
          this.notify.success('Permission removed successfully');
          this.store.dispatch(
            new TableActions.UpdateRolePermission({
              roleId: roleId,
              permission: permission,
              isAdd: false,
            })
          );
          this.store.dispatch(new AuthActions.LoadPermissions())
        },
        error: (err) => {
          this.notify.error(err.error.message);
        },
      });

    this.subs.push(sub);
  }


  groupPermissionsByPathSegment(permissions: any[]) {
    const grouped: { [key: string]: any[] } = {};

    for (const permission of permissions) {
      const pathParts = permission.path?.split('/');
      const group = pathParts[3];
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(permission);
    }

    this.groupedPermissions = grouped;
    this.filteredGroupedPermissions = grouped;
    // Default all  groups to collaped
    this.permissionGroupState = Object.fromEntries(
      Object.keys(grouped).map((key) => [key, false])
    );
    // permission loading and grouping completed
    this.isFetchingPermissions$ = new Observable<false>;
  }

  get filteredGroupKeys(): string[] {
    return Object.keys(this.filteredGroupedPermissions || {});
  }

  listenToPermissionSearch() {
    const sub = this.route.queryParams.subscribe((params) => {
      const search = params['search'];
      this.searchKeyword = search || '';

      if (!search) {
        this.filteredGroupedPermissions = this.groupedPermissions;
        //if no search, close dropdowns
        this.permissionGroupState = Object.fromEntries(
        Object.keys(this.groupedPermissions).map((key) => [key, false])
      );
        return;
      }

      const keyword = search.toLowerCase();
      const filtered: { [key: string]: any[] } = {};

      // First, close all groups
      this.permissionGroupState = Object.fromEntries(
        Object.keys(this.groupedPermissions).map((key) => [key, false])
      );

      for (const group of Object.keys(this.groupedPermissions)) {
        const permissions = this.groupedPermissions[group];

        const matches = permissions.filter((permission) =>
          permission.name.toLowerCase().includes(keyword)
        );

        if (matches.length > 0) {
          filtered[group] = matches;

          // only open groups containing matches
          this.permissionGroupState[group] = true;
        }
      }

      this.filteredGroupedPermissions = filtered;
    });

    this.subs.push(sub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
